import { useEffect, useCallback, useMemo } from 'react';
import { nanoid } from 'nanoid';

import { useImmerReducer } from '~/theseus/hooks/useImmer';

const determineNextSelected = (draft, keyCode) => {
  const selectedIndex = draft.itemIds.findIndex(
    item => item === draft.selected,
  );
  switch (keyCode) {
    // ArrowLeft
    case 37: {
      if (selectedIndex - 1 >= 0) {
        draft.selected = draft.itemIds[selectedIndex - 1];
      }
      break;
    }
    // ArrowUp
    case 38: {
      if (selectedIndex - draft.columns >= 0) {
        draft.selected = draft.itemIds[selectedIndex - draft.columns];
      }
      break;
    }
    // ArrowRight
    case 39: {
      if (selectedIndex + 1 < draft.itemIds.length) {
        draft.selected = draft.itemIds[selectedIndex + 1];
      }
      break;
    }
    // ArrowDown
    case 40: {
      if (selectedIndex + draft.columns < draft.itemIds.length) {
        draft.selected = draft.itemIds[selectedIndex + draft.columns];
      }
      break;
    }
    default:
  }
};

const reducer = (draft, action) => {
  switch (action.type) {
    case 'KEY_PRESS': {
      determineNextSelected(draft, action.payload);
      break;
    }
    case 'UPDATE_COLUMNS': {
      if (action.payload > 0 && action.payload <= draft.itemIds.length) {
        draft.columns = action.payload;
      }
      break;
    }
    case 'UPDATE_SELECTED': {
      if (action.payload) {
        draft.selected = action.payload;
      }
      break;
    }
    case 'UPDATE_ITEMIDS': {
      if (action.payload) {
        draft.itemIds = action.payload;
      }
      break;
    }
    case 'SELECT_ITEM': {
      draft.selected = action.payload;
      break;
    }
    default:
      break;
  }
};

/**
 * Hook used to manage grid state and arrow navigation
 * @param {Array} itemIds - Ids of all children in grid. This is extracted from the children of Grid upon rendering
 * @param {Object} [options]
 * @param {string|number} [options.selected=null] - The element to have selected that is controlled by the parent component. If null, then the first element of the itemIds array will be selected
 * @param {number} [options.columns=2] - Columns of the grid you would like
 * @param {string} [options.id=grid] - Id of the grid. This will be used to prefix all children of the grid to provide unique identifiers
 */
export default function useGrid(itemIds, options = {}) {
  const {
    selected = null,
    columns = 2,
    idPrefix = 'grid',
  } = options;

  // This will ensure that if two grids have the same id, they will always be unique
  const defaultSelected = useMemo(() => {
    if (selected) {
      return selected;
    }

    if (Array.isArray(itemIds) && itemIds.length > 0) {
      return itemIds[0];
    }

    return null;
  }, [selected]);

  const [state, send] = useImmerReducer(reducer, {
    itemIds,
    selected: defaultSelected,
    columns,
  });

  useEffect(() => {
    send({ type: 'UPDATE_SELECTED', payload: selected });
  }, [selected]);

  useEffect(() => {
    send({ type: 'UPDATE_COLUMNS', payload: columns });
  }, [columns]);

  useEffect(() => {
    send({ type: 'UPDATE_ITEMIDS', payload: itemIds });
  }, [itemIds]);

  const handleKeyPress = useCallback(
    e => send({ type: 'KEY_PRESS', payload: e.keyCode }),
    [],
  );
  const selectItem = useCallback(
    itemId => send({ type: 'SELECT_ITEM', payload: itemId }),
    [],
  );

  const id = useMemo(() => `${idPrefix}-${nanoid()}`, [idPrefix]);

  return {
    selected: state.selected,
    id,
    handleKeyPress,
    selectItem,
  };
}
